import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { primaryBtnTeal } from "../../style/PatientDetailsStyle";
import { menuItemStyle, textfieldText } from "../../style/AppointmentStyle";
import { headingStyle } from "../../style/LapPageStyle";
import CloseIcon from "@mui/icons-material/Close";
import { showToast } from "../../utils/toastUtil";
import { postLedgerEntries } from "../../connections/postApi";
import UseFetchSupplierList from "../../helper/UseFetchSupplierList";
import useDebounce from "../../hooks/UseDebounce";

// ====== COMPONENT ======
const AddlegderEntries = ({ onClose, id, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [itemPerPage] = useState(10);
  const [page] = useState(1);

  const [supplierInput, setSupplierInput] = useState("");
  const debouncedInputSupplier = useDebounce(supplierInput, 800);

  // const {
  //   data: supplierList,
  //   refetch,
  //   isLoading: isSupplierLoading,
  // } = UseFetchSupplierList(page, itemPerPage, debouncedInputSupplier);

  // ✅ safe suppliers array (without breaking existing flow)
  // const suppliers = Array.isArray(supplierList?.payload?.suppliers)
  //   ? supplierList.payload.suppliers
  //   : Array.isArray(supplierList?.payload)
  //     ? supplierList.payload
  //     : [];

  // const [selectedSupplierId, setSelectedSupplierId] = useState("");

  const [form, setForm] = useState({
    paid_to_type: "Supplier",
    paid_to_id: id, // ✅ this is supplier id
    entry_type: "payment_supplier", // static
    amount: "",
    paid_at: dayjs().format("YYYY-MM-DD"),
    payment_mode: "Cash",
    bill_no: "",
    remarks: "",
  });

  const paymentModes = useMemo(() => ["Cash", "UPI", "Card", "Cheque"], []);

  const handleChange = (key) => (e) => {
    setError("");
    setSuccessMsg("");
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // const handleSupplierChange = (e) => {
  //   e.preventDefault();
  //   setSupplierInput(e?.target?.value || "");
  // };

  const buildPayload = () => ({
    ledger_entry: {
      paid_to_type: "Supplier",
      amount: form?.amount,
      paid_to_id: String(id || form.paid_to_id || ""), // ✅ supplier id
      entry_type: form.entry_type,
      bill_no: form.bill_no,
      paid_at: form?.paid_at,
      // remarks:
      //   form.remarks || `Debit ${form.bill_no || ""} ${form.paid_at}`.trim(),
    },
  });

  const validate = () => {
    if (!form.paid_to_id) return "Supplier is required";
    if (!form.amount || Number(form.amount) <= 0) return "Amount must be > 0";
    if (!form.paid_at) return "Paid At date is required";
    if (!form.payment_mode) return "Payment mode is required";
    if (!form.bill_no) return "Bill No is required";
    return "";
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const payload = buildPayload();
      const res = await postLedgerEntries(payload);
      if (res?.status?.status === "SUCCESS") {
        showToast("Ledger entry created successfully", "SUCCESS");
        refetch();
        onClose();
        setSuccessMsg("Ledger entry created successfully.");
        // reset (keep type/mode/date same)
        // setSelectedSupplierId("");
        setSupplierInput("");
        setForm((prev) => ({
          ...prev,
          paid_to_id: "",
          amount: "",
          bill_no: "",
          remarks: "",
        }));
      } else {
        showToast(
          res?.status?.message || "Failed to create ledger entry",
          res?.status?.status || "ERROR",
        );
      }
    } catch (e) {
      setError(e?.message || "Something went wrong");
      showToast(e?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // NOTE: kept your computed totals as-is (not used in UI)
  // const totalRecords = supplierList?.meta?.total_count || 0;
  // const totalPage = Math.ceil(totalRecords / itemPerPage);

  return (
    <Box sx={{ p: 2, mx: "auto" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
      >
        <Box></Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="heading_Large"
            fontWeight={700}
            gutterBottom
            sx={headingStyle}
          >
            Add Ledger Entry
          </Typography>
        </Box>
        <IconButton sx={{ marginBottom: "2rem" }} onClick={onClose}>
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {successMsg ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* keep your existing dropdown (type) */}
        {/* <TextField
          select
          label="Select Supplier"
          value={form.paid_to_type}
          onChange={handleChange("paid_to_type")}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={textfieldText}
        >
          <MenuItem value="Supplier">Supplier</MenuItem>
          <MenuItem value="Customer">Customer</MenuItem>
          <MenuItem value="Employee">Employee</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField> */}

        {/* keep entry type */}
        <TextField
          select
          sx={textfieldText}
          label="Entry Type"
          value={form.entry_type}
          InputLabelProps={{ shrink: true }}
          onChange={handleChange("entry_type")}
          fullWidth
        >
          {["payment_supplier", "refund_purchase"].map((m) => (
            <MenuItem
              key={m}
              value={m}
              sx={{ ...menuItemStyle, textTransform: "capitalize" }}
            >
              {m === "payment_supplier"
                ? "Payment to Supplier"
                : "Payment From Supplier"}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Amount"
          type="number"
          sx={textfieldText}
          InputLabelProps={{ shrink: true }}
          value={form.amount}
          onChange={handleChange("amount")}
          fullWidth
          placeholder="e.g. 6000"
          inputProps={{ min: 0, step: "0.01" }}
        />

        <TextField
          label="Paid At"
          type="date"
          sx={textfieldText}
          value={form.paid_at}
          onChange={handleChange("paid_at")}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          sx={textfieldText}
          label="Payment Mode"
          value={form.payment_mode}
          InputLabelProps={{ shrink: true }}
          onChange={handleChange("payment_mode")}
          fullWidth
        >
          {paymentModes.map((m) => (
            <MenuItem key={m} value={m} sx={menuItemStyle}>
              {m}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Bill No"
          sx={textfieldText}
          value={form.bill_no}
          onChange={handleChange("bill_no")}
          fullWidth
          InputLabelProps={{ shrink: true }}
          placeholder="eg : Bill NO 001"
        />
        {/* 
        <TextField
          sx={textfieldText}
          label="Remarks"
          value={form.remarks}
          InputLabelProps={{ shrink: true }}
          onChange={handleChange("remarks")}
          fullWidth
          placeholder='e.g. "Debit Bill NO 001 2026-02-23"'
        /> */}
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={primaryBtnTeal}
        >
          {loading ? (
            <Typography variant="body2">Saving...</Typography>
          ) : (
            <Typography variant="body2">Create Entry</Typography>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default AddlegderEntries;
